import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { cloneDeep } from 'lodash';

import { getClass } from '../../../utils/component-helpers';

import Column from './column';
import { CheckboxCell } from './cells';
import Icon from '../icon';
import DragItem from '../../misc/drag-drop-item';
import DropZone from '../../misc/drag-drop-list';

import './table-v2.scss';
import { formatCellValue } from '../../base/common/utils';
import Tooltip from '../../misc/tooltip';

const Table = props => {
  const {
    children,
    data,
    onAllRowsSelect,
    onRowSelect,
    onRowClick,
    onSort,
    // rowHeight,
    rowIdField,
    rowPath,
    selectable,
    selectedColors,
    selectLimit,
    selectedRows,
    sortField,
    sortOrder,
    stickyColumnsCount,
    totals,
    visibleColumns,
    orderable,
    setData,
  } = props;

  const [shadows, setShadows] = useState(false);

  const scrollDiv = useRef(null);
  const columns = React.Children.toArray(children);
  const selectedCount =
    selectable && selectedRows && Object.values(selectedRows).filter(val => val).length;

  const onTableScroll = e => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target;
    const left = scrollLeft > 0;
    const right = clientWidth + scrollLeft < scrollWidth;
    if (shadows.left !== left || shadows.right !== right) {
      setShadows({ left, right });
    }
  };

  const setSort = field => {
    let order;
    if (sortField !== field) {
      order = 'desc';
    } else {
      order = sortOrder === 'desc' ? 'asc' : 'desc';
    }

    onSort({ field, order });
  };

  const columnJustification = justifyContent => {
    return {
      'left-justify': justifyContent === 'left',
      'center-justify': justifyContent === 'center',
      'right-justify': justifyContent === 'right',
    };
  };

  const handleDragDrop = (item, dropZoneIndex, relativePosition) => {
    const newData = cloneDeep(data);
    const newIndex = relativePosition === 'lower' ? dropZoneIndex + 1 : dropZoneIndex;

    newData[item.index] = null;
    newData.splice(newIndex, 0, data[item.index]);
    const filtered = newData.filter(datum => datum !== null);
    setData(filtered);
  };

  const HeaderRow = () => {
    let stickyWidth = selectable ? 38 : 0;

    return (
      <tr className="header-row">
        {//	TODO: Please fix.
        selectable ? onAllRowsSelect ? <CheckboxCell /> : <th className="column checkbox" /> : null}
        {columns.map((col, columnIndex) => {
          const {
            sortable,
            headerIcon,
            label,
            // headerOverride,
            dataKey,
            justifyContent,
            width,
            flexWidth,
            isShowing,
            labelRender,
          } = col.props;

          if ((visibleColumns && !visibleColumns.has(dataKey)) || !isShowing) {
            return null;
          }
          const labelContent = labelRender ? labelRender({ label }) : label;

          const getSortIcon = () => {
            if (!sortable || sortField !== dataKey) return null;
            const sortDirection = sortOrder === 'desc' ? 'caretDown' : 'caretUp';
            return (
              <span className="sort-icon">
                <Icon size={14} iconName={sortDirection} />
              </span>
            );
          };

          const isSticky = columnIndex < stickyColumnsCount;
          const addShadow = shadows.left && isSticky && columnIndex === stickyColumnsCount - 1;
          const className = getClass('column', dataKey, { shadow: addShadow });
          const stickyStyles = isSticky
            ? {
                position: 'sticky',
                left: stickyWidth,
              }
            : {};
          stickyWidth += width;

          // if (headerOverride) {
          // 	return <th className={className}>{headerOverride(col)}</th>;
          // }

          const widthStyles = !flexWidth
            ? {
                minWidth: width && `${width}px`,
                ...stickyStyles,
              }
            : {
                flexGrow: `${width}`,
                flexBasis: `${width}px`,
                flexShrink: 0,
                ...stickyStyles,
              };

          return (
            <th
              className={className}
              key={`${columnIndex}-header-${dataKey}`}
              onClick={() => sortable && setSort(dataKey)}
              style={{
                cursor: sortable ? 'pointer' : 'default',
                ...widthStyles,
              }}
            >
              <div className={getClass('cell-content', columnJustification(justifyContent))}>
                {labelContent}
                {headerIcon && <Icon iconName={headerIcon} />}
                {getSortIcon()}
              </div>
            </th>
          );
        })}
      </tr>
    );
  };

  const StatRow = ({ key, rowData, rowIndex, selected }) => {
    let stickyWidth = selectable ? 38 : 0;

    return (
      <tr key={key} className={getClass('stat-row')} onClick={() => onRowClick(rowIndex)}>
        {selectable && (
          <CheckboxCell
            checked={!!selected}
            color={selectedColors[rowData[rowIdField] || '']}
            onSelect={() => {
              if (selectLimit && !selected && selectedCount >= selectLimit) return;
              onRowSelect({
                rowData,
                rowId: rowData[rowIdField],
              });
            }}
          />
        )}
        {columns.map((col, columnIndex) => {
          const {
            cellRender,
            dataKey,
            justifyContent,
            format,
            width,
            flexWidth,
            tooltip,
            fontColor,
            isShowing,
          } = col.props;

          if ((visibleColumns && !visibleColumns.has(dataKey)) || !isShowing) {
            return null;
          }

          let cellData = rowData[dataKey];

          if (cellData && format) {
            cellData = formatCellValue(cellData, format.type, format.options);
          }

          const content = cellRender
            ? cellRender({ cellData, rowData, rowIndex, columnIndex, columnInfo: col })
            : typeof cellData === 'undefined' || cellData === null
            ? '-'
            : String(cellData);

          const isSticky = columnIndex < stickyColumnsCount;
          const addShadow = shadows.left && isSticky && columnIndex === stickyColumnsCount - 1;
          const className = getClass('column', dataKey, { shadow: addShadow });
          const stickyStyles = isSticky ? { position: 'sticky', left: stickyWidth } : {};
          stickyWidth += width;
          const widthStyles = !flexWidth
            ? {
                minWidth: width && `${width}px`,
                ...stickyStyles,
              }
            : {
                flexGrow: `${width}`,
                flexBasis: `${width}px`,
                flexShrink: 0,
                ...stickyStyles,
              };

          const columnContent =
            rowPath && !columnIndex ? <Link to={rowPath(rowData)}>{content}</Link> : content;

          return (
            <td
              key={`${rowIndex}-${columnIndex}-${dataKey}`}
              className={className}
              style={widthStyles}
            >
              <div
                className={getClass(
                  `cell-content ${fontColor ? fontColor(cellData) : ''}`,
                  columnJustification(justifyContent),
                )}
                ref={node => {
                  return (
                    tooltip &&
                    new Tooltip(node, tooltip({ cellData, rowData, rowIndex, columnIndex }))
                  );
                }}
              >
                {columnContent}
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  const TotalsRow = ({ data }) => {
    let stickyWidth = selectable ? 38 : 0;
    return (
      <tr className="stat-row">
        {selectable && <td className="column checkbox" />}
        {columns.map((col, columnIndex) => {
          const {
            dataKey,
            justifyContent,
            width,
            totalRender,
            format,
            flexWidth,
            isShowing,
          } = col.props;

          if ((visibleColumns && !visibleColumns.has(dataKey)) || !isShowing) {
            return null;
          }

          let cellData = data[dataKey];

          if (cellData && format) {
            cellData = formatCellValue(cellData, format.type, format.options);
          }

          let content = '';
          if (totalRender) {
            content = totalRender({ cellData, columnIndex, columnInfo: col, rowData: data });
          } else if (columnIndex === 0) {
            content = 'Total';
          } else if (typeof cellData === 'undefined' || cellData === null) {
            content = '-';
          } else {
            content = String(cellData);
          }

          const isSticky = columnIndex < stickyColumnsCount;
          const addShadow = shadows.left && isSticky && columnIndex === stickyColumnsCount - 1;
          const className = getClass('column', dataKey, { shadow: addShadow });
          const stickyStyles = isSticky
            ? {
                position: 'sticky',
                left: stickyWidth,
              }
            : {};
          stickyWidth += width;
          const widthStyles = !flexWidth
            ? {
                minWidth: width && `${width}px`,
                ...stickyStyles,
              }
            : {
                flexGrow: `${width}`,
                flexBasis: `${width}px`,
                flexShrink: 0,
                ...stickyStyles,
              };

          return (
            <td key={`total-${dataKey}`} className={className} style={widthStyles}>
              <div
                className={getClass(
                  'totals',
                  'cell-content',
                  columnJustification(justifyContent),
                  columnIndex === 0 ? 'total-header' : null,
                )}
              >
                {content}
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="table-container-v2">
      <table className="table-v2" onScroll={onTableScroll}>
        <thead className="table-header">{HeaderRow()}</thead>
        <tbody className="table-body" ref={scrollDiv}>
          {totals && TotalsRow({ data: totals })}
          {data.map((rowData, index) => {
            const statRow = StatRow({
              key: rowData[rowIdField],
              rowData,
              rowIndex: index,
              selected: selectedRows && !!selectedRows[rowData[rowIdField]],
            });
            return orderable ? (
              <DragItem index={index} key={rowData[rowIdField]}>
                <DropZone
                  moveListItem={(listIndex, item) => handleDragDrop(item, index, 'above')}
                ></DropZone>
                <DropZone
                  moveListItem={(listIndex, item) => handleDragDrop(item, index, 'lower')}
                ></DropZone>
                {statRow}
              </DragItem>
            ) : (
              statRow
            );
          })}
        </tbody>
      </table>
      {shadows.right && <div className="shadow-right" />}
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape).isRequired,
  totals: PropTypes.object,
  stickyColumnsCount: PropTypes.number,
  rowIdField: PropTypes.string.isRequired,
  rowPath: PropTypes.func,
  selectLimit: PropTypes.number,
  selectable: PropTypes.bool,
  selectedColors: PropTypes.shape({ id: PropTypes.string, color: PropTypes.string }),
  selectedRows: PropTypes.object,
  onSort: PropTypes.func,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  sortField: PropTypes.string,
  children: PropTypes.arrayOf(Column).isRequired,
  onAllRowsSelect: PropTypes.func,
  onRowSelect: PropTypes.func,
  onRowClick: PropTypes.func,
  visibleColumns: PropTypes.instanceOf(Set),
  orderable: PropTypes.bool,
  setData: PropTypes.func,
};

Table.defaultProps = {
  onAllRowsSelect: null,
  onRowSelect: null,
  onRowClick: () => {},
  totals: null,
  sortField: null,
  sortOrder: null,
  onSort: null,
  rowPath: null,
  selectLimit: null,
  selectedRows: {},
  selectedColors: {},
  selectable: false,
  stickyColumnsCount: 0,
  visibleColumns: null,
  orderable: false,
  setData: () => {},
};

export default Table;
